import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchCollection, fetchEntryBySlug } from '../lib/cms';
import ContactForm from '../components/ContactForm';

export default function Kontakt() {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const contactItems = await fetchCollection('contact_page');
        const page = (Array.isArray(contactItems) && contactItems[0]) || await fetchEntryBySlug('da', 'kontakt');
        setPageData(page);
      } catch (err) {
        console.error('Error loading kontakt data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const metaTitle = pageData?.meta_title?.da || pageData?.meta_title || "Kontakt SKT Byg | Ring 20 91 00 19 | Tømrer i Galten & omegn";
  const metaDesc = pageData?.meta_description?.da || pageData?.meta_description || "Har du et byggeprojekt i Galten, Silkeborg, Aarhus eller omegn? Kontakt tømrermester Steffen K. Termannsen på tlf. 20 91 00 19 for uforpligtende rådgivning.";

  const pageTitle = pageData?.page_title?.da || pageData?.page_title || "Kontakt";
  const pageSubtitle = pageData?.page_subtitle?.da || pageData?.page_subtitle || "Du er altid velkommen til at kontakte os. Er der noget du er i tvivl om, eller søger du råd og vejledning? Vi vil hellere end gerne besvare dine spørgsmål.";
  const infoTitle = pageData?.contact_info_title?.da || pageData?.contact_info_title || pageData?.contact_title?.da || "Kontaktinformation";
  const infoText = pageData?.contact_info_text?.da || pageData?.contact_info_text || pageData?.contact_text?.da || "Ring eller send os en mail, så vender vi tilbage snarest muligt.";
  const addressLabel = pageData?.address?.da || pageData?.address || pageData?.address_label?.da || "Filippavænget 12, 8464 Galten";
  const phoneLabel = pageData?.phone?.da || pageData?.phone || pageData?.phone_label?.da || "20 91 00 19";
  const emailLabel = pageData?.email?.da || pageData?.email || pageData?.email_label?.da || "steffen@skt-byg.dk";
  const cvrLabel = pageData?.cvr?.da || pageData?.cvr || pageData?.cvr_label?.da || "30587944";
  const contactImage = (pageData?.image && pageData.image[0]) || "https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/slider-2.jpg";

  return (
    <div className="min-h-screen bg-brand-light py-16">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/47bb99a3-111d-47bf-9e96-8ebfe359b0ab/slider-2.jpg" />
      </Helmet>

      <div className="mx-auto max-w-7xl px-6">
        {/* Sidetitel */}
        <div className="text-left max-w-3xl mb-16">
          <h1 className="text-4xl md:text-5xl font-extrabold text-brand-dark mb-4">
            {pageTitle}
          </h1>
          <p className="text-lg text-brand-dark/70 leading-relaxed">
            {pageSubtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Kontaktinfo */}
          <div className="lg:col-span-5 space-y-8">
            <div>
              <h2 className="text-2xl font-extrabold text-brand-dark">
                {infoTitle}
              </h2>
              <div 
                className="text-brand-dark/70 text-sm leading-relaxed mt-2 prose-sm prose-p:my-1"
                dangerouslySetInnerHTML={{ __html: infoText }}
              />
            </div>

            <div className="divide-y divide-brand-sand">
              {/* Adresse */}
              <div className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
                <div className="flex h-8 w-8 shrink-0 items-center justify-start text-brand-accent">
                  <i className="fa-solid fa-map-marker-alt text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-brand-dark">{addressLabel}</p>
                </div>
              </div>

              {/* Telefon */}
              <div className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
                <div className="flex h-8 w-8 shrink-0 items-center justify-start text-brand-accent">
                  <i className="fa-solid fa-phone text-lg"></i>
                </div>
                <div>
                  <a href={`tel:${phoneLabel.replace(/\s+/g, '')}`} className="text-sm font-bold text-brand-dark hover:text-brand-accent transition-colors whitespace-nowrap">
                    {phoneLabel}
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
                <div className="flex h-8 w-8 shrink-0 items-center justify-start text-brand-accent">
                  <i className="fa-solid fa-envelope text-lg"></i>
                </div>
                <div>
                  <a href={`mailto:${emailLabel}`} className="text-sm font-bold text-brand-dark hover:text-brand-accent transition-colors whitespace-nowrap">
                    {emailLabel}
                  </a>
                </div>
              </div>

              {/* CVR */}
              <div className="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
                <div className="flex h-8 w-8 shrink-0 items-center justify-start text-brand-accent">
                  <i className="fa-solid fa-id-card text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-brand-dark">{cvrLabel}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Formular */}
          <div className="lg:col-span-7">
            <ContactForm subject="Henvendelse fra Kontaktsiden" />
          </div>
        </div>
      </div>
    </div>
  );
}
